import React, { useCallback, useMemo } from 'react';
import type { Action, ActionFn, SetIsLoading, Filter } from '#/common/components/DataGrid';
import DataGrid from '#/common/components/DataGrid';
import columns from './columns';
import { actions } from './actions';
import { gql } from '@apollo/react-hooks';
import { getFindingList } from '#/graphql/queries';
import useFindingDelete from '../../hooks/useDeleteFindingMutation';
import useToast from '#/common/hooks/useToast';
import { FindingStatus, Finding } from '#/graphql/types';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { get } from 'lodash';
import getPermanentFilterValue from '#/common/utils/parsers/parsePermanentFilters';

const getFindingListQuery = gql(getFindingList);

const DISABLED_WO_STATUSES = [
  FindingStatus.wo_created,
  FindingStatus.in_review,
  FindingStatus.resolved,
];

interface FindingDataGridProps {
  omitColumns?: string[];
  hideNewAction?: boolean;
  permanentFilters?: Filter[];
}

const FindingDataGrid: React.FC<FindingDataGridProps> = ({
  omitColumns,
  hideNewAction,
  permanentFilters,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assetId =
    searchParams.get('assetId') ||
    getPermanentFilterValue('assetId', permanentFilters) ||
    undefined;
  const tenantId =
    searchParams.get('tenantId') ||
    getPermanentFilterValue('tenantId', permanentFilters) ||
    undefined;

  const { confirm, alert } = useToast();

  const { deleteFinding } = useFindingDelete();

  const deleteItem = useCallback(
    async (selectedItems: Finding[], { setIsLoading }: { setIsLoading?: SetIsLoading }) => {
      if (!selectedItems || selectedItems.length === 0) {
        return undefined;
      }

      // Filter items that can be deleted (only "new" status)
      const deletableItems = selectedItems.filter(
        (item: Finding) => item.status === FindingStatus.new,
      );
      const nonDeletableItems = selectedItems.filter(
        (item: Finding) => item.status !== FindingStatus.new,
      );

      if (deletableItems.length === 0) {
        await alert(
          'warning',
          'None of the selected findings can be deleted. Only findings with "New" status can be deleted.',
        );
        return;
      }

      let confirmMessage = '';
      if (nonDeletableItems.length > 0) {
        confirmMessage = `${deletableItems.length} of ${selectedItems.length} selected findings will be deleted. Only findings with "New" status can be deleted. Continue?`;
      } else {
        confirmMessage = `Are you sure you want to delete ${deletableItems.length} finding${deletableItems.length > 1 ? 's' : ''}?`;
      }

      const sure = await confirm('error', confirmMessage);
      if (!sure) {
        return;
      }

      setIsLoading && setIsLoading(true, 'Deleting data...');
      // Delete only the deletable items
      const idsToDelete = deletableItems.map((item: Finding) => item.id);
      await deleteFinding(idsToDelete.join(','));
      setIsLoading && setIsLoading(true, 'Deleting data...');
    },
    [deleteFinding, confirm, alert],
  );

  const createWorkOrderFromFinding = useCallback(
    (selectedFinding: Finding) => {
      // Navigate to work order creation with findingId and assetId
      navigate(
        `/work-orders/new?findingId=${selectedFinding.id}&assetId=${selectedFinding.assetId}&tenantId=${selectedFinding.tenantId}`,
      );
    },
    [navigate],
  );

  const findingSelectedActions: ActionFn<Finding>[] = [
    {
      key: 'delete-findings',
      icon: 'trash',
      text: 'Delete',
      onClick: async (e) => {
        await deleteItem(e.selectedItems, { setIsLoading: e.setIsLoading });
        e?.setIsLoading && e.setIsLoading(false);
        e.dg?.instance.clearSelection();
        e.dg?.instance.refresh();
      },
    },
    {
      key: 'create-work-order',
      icon: 'wrench-simple',
      text: 'Create Work Order',
      disabled: (e) => {
        // Active only when exactly one record is selected and its status is not one of the DISABLED_WO_STATUSES
        return (
          !e.selectedItems ||
          e.selectedItems.length !== 1 ||
          DISABLED_WO_STATUSES.includes(e.selectedItems[0].status as FindingStatus)
        );
      },
      onClick: (e) => {
        createWorkOrderFromFinding(e.selectedItems[0]);
        e.dg?.instance.clearSelection();
      },
    },
  ];

  const cols = useMemo(
    () => columns.filter((c) => !(omitColumns || []).includes(c.dataField)),
    [omitColumns],
  );

  return (
    <DataGrid
      id="findings"
      pluralText="Findings"
      singularText="Finding"
      columns={cols}
      recordUrl="/findings/:id"
      actions={actions}
      selectedActions={findingSelectedActions}
      getItemsQuery={getFindingListQuery}
      permanentFilters={permanentFilters}
    />
  );
};

export default React.memo(FindingDataGrid);
